'use client'

import { ActivityHeatmap } from '@excellent/ui/blocks/lens/overview/activity-heatmap'
import { PeakHoursChart } from '@excellent/ui/blocks/lens/overview/peak-hours-chart'
import { DayOfWeekChart } from '@excellent/ui/blocks/lens/activity/day-of-week-chart'
import { UsageOverTimeChart } from '@excellent/ui/blocks/lens/overview/usage-over-time-chart'
import { Card, CardContent } from "@excellent/ui"
import { SectionTitle } from "@excellent/ui"
import { InfoHint } from "@excellent/ui"
import { StatStrip, type StatStripItem } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { AlertTriangle } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'

export default function ActivityPage() {
 const { snapshot, error, isLoading } = useSnapshot()
 const data = snapshot?.activity
 const scopedProject = snapshot?.scopedProject ?? null

 const hourCounts = data
 ? Object.fromEntries(data.hour_counts.map(h => [String(h.hour), h.count]))
 : {}

 return (
 <div className="flex flex-col">
 <div className="w-full space-y-4">

 {error && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 )}

 {isLoading && (
 <>
 {/* Activity calendar (title + stat band + graph) */}
 <Skeleton className="h-72 min-h-72 rounded-lg" />
 {/* Peak hours | Day of week */}
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch">
 <Skeleton className="h-64 min-h-64 rounded-lg" />
 <Skeleton className="h-64 min-h-64 rounded-lg" />
 </div>
 {/* Usage over time */}
 <Skeleton className="h-64 rounded-lg" />
 </>
 )}

 {data && (() => {
 const statItems: StatStripItem[] = [
 {
 title: "Current Streak",
 value: data.streaks.current.toLocaleString(),
 description: "consecutive days",
 },
 {
 title: "Longest Streak",
 value: data.streaks.longest.toLocaleString(),
 description: "personal best",
 },
 {
 title: "Active Days",
 value: data.total_active_days.toLocaleString(),
 description: "total days with activity",
 },
 {
 title: "Most Active Day",
 value: data.most_active_day ? data.most_active_day.slice(5) : '—',
 description: data.most_active_day_msgs ? `${data.most_active_day_msgs.toLocaleString()} messages` : 'no data',
 },
 ]
 return (
 <>
 {/* Activity calendar — the streak metrics are banded across the card
     head (StatStrip variant="table"), sitting between the title chrome
     row and the GitHub-style contribution graph so the numbers read as
     part of the same calendar, not a separate floating strip. */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle
 variant="card"
 title="Activity Calendar"
 subtitle={scopedProject ? `Scoped to ${scopedProject.name}` : undefined}
 />
 <StatStrip items={statItems} variant="table" />
 <CardContent className="py-4">
 <ActivityHeatmap data={data.daily_activity} />
 </CardContent>
 </Card>

 {/* Row: Peak hours | Day of week. Both card bodies share one fixed height
     (h-56) and each chart fills it (height="100%") — so the two cards match
     and no chart leaves empty space below it. The Peak Hours explainer moved
     off the chart footer into the title's (i) affordance. */}
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle
 variant="card"
 title={
 <span className="inline-flex items-center gap-1.5">
 Peak Hours
 <InfoHint>
 Session start times across the 24-hour day. The three busiest hours are highlighted.
 </InfoHint>
 </span>
 }
 />
 <CardContent className="h-56 py-4">
 <PeakHoursChart hourCounts={hourCounts} height="100%" />
 </CardContent>
 </Card>

 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Day of Week" />
 <CardContent className="h-56 py-4">
 <DayOfWeekChart data={data.dow_counts} height="100%" />
 </CardContent>
 </Card>
 </div>

 {/* Usage over time — full-width line chart, fills a taller fixed body. */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Usage Over Time" />
 <CardContent className="h-72 py-4">
 <UsageOverTimeChart data={data.daily_activity} days={90} height="100%" />
 </CardContent>
 </Card>
 </>
 )
 })()}
 </div>
 </div>
 )
}
